// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo, useContext } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { AuthContext } from '../../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import './ChartStyles.scss';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Material Distribution Chart Component
 * 
 * Shows the total weight of deliveries per material type as a horizontal bar chart
 */
function MaterialDistributionChart({ deliveries = [] }) {
  const { labelsets } = useContext(AuthContext);
  const { t } = useTranslation();
  
  // Function to get material display name
  const getMaterialDisplayName = (materialKey) => {
    return labelsets?.materials?.labels?.[materialKey]?.display_name || materialKey || 'Unknown';
  };
  
  // Process deliveries to aggregate by material type
  const chartData = useMemo(() => {
    if (!deliveries || deliveries.length === 0) {
      return { labels: [], values: [], colors: [] };
    }

    // Group deliveries by material type and sum weights
    const materialWeights = new Map();
    
    deliveries.forEach(delivery => {
      const materialKey = delivery.base_labels?.materials;
      if (!materialKey) return;
      
      // Get weight from delivery data
      const weightKg = delivery.documents_data?.complete_data?.weight_kg_netto_2 
        ? parseFloat(delivery.documents_data.complete_data.weight_kg_netto_2) 
        : 0;
      
        const currentWeight = materialWeights.get(materialKey) || 0;
        materialWeights.set(materialKey, currentWeight + weightKg);
    });
    
    // Sort materials by weight (descending)
    const sortedMaterials = [...materialWeights.entries()]
      .sort((a, b) => b[1] - a[1]);
    
    return {
      labels: sortedMaterials.map(([key]) => getMaterialDisplayName(key)),
      values: sortedMaterials.map(([_, weight]) => weight / 1000), // Convert to tons
      colors: sortedMaterials.map((_, index) => {
        // Generate colors based on index
        const colors = [
          'rgba(37, 99, 235, 0.7)',     // Blue
          'rgba(59, 130, 246, 0.7)',    // Light Blue
          'rgba(14, 165, 233, 0.7)',    // Sky Blue
          'rgba(6, 182, 212, 0.7)',     // Cyan
          'rgba(20, 184, 166, 0.7)',    // Teal
          'rgba(16, 185, 129, 0.7)',    // Emerald
          'rgba(5, 150, 105, 0.7)',     // Green
          'rgba(4, 120, 87, 0.7)',      // Dark Green
        ];
        return colors[index % colors.length];
      })
    };
  }, [deliveries, labelsets]);

  // Chart.js options for horizontal bar chart
  const options = {
    indexAxis: 'y', // Horizontal bar chart
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            return tooltipItems[0].label;
          },
          label: (context) => {
            return `${t('weight_tons')}: ${context.raw.toFixed(2)} ${t('tons')}`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
        },
        title: {
          display: true,
          text: t('weight_tons'),
          color: '#666',
          font: {
            size: 12,
          },
        },
      },
      y: {
        grid: {
          display: false,
        },
        // Limit the height by controlling bar thickness and padding
        afterFit: (scaleInstance) => {
          scaleInstance.height = Math.min(scaleInstance.height, 250);
        }
      },
    },
    // Limit overall chart size
    onResize: (chart, size) => {
      if (size.height > 300) {
        chart.height = 300;
      }
    }
  };

  // Chart.js data config
  const data = {
    labels: chartData.labels,
    datasets: [
      {
        label: t('weight_tons'),
        data: chartData.values,
        backgroundColor: chartData.colors,
        borderColor: chartData.colors.map(color => color.replace('0.7', '1')),
        borderWidth: 1,
        borderRadius: 4,
        barThickness: 'flex',
        maxBarThickness: 35,
      },
    ],
  };

  return (
    <div className="material-distribution-chart">
      {deliveries && deliveries.length > 0 ? (
        <div className="chart-container">
          <Bar options={options} data={data} height={300} />
        </div>
      ) : (
        <div className="no-data-message">
          {t('no_material_data')}
        </div>
      )}
    </div>
  );
}

export default MaterialDistributionChart; 